import React, {Fragment, useState} from "react";
import DataGridInternal from "./DataGrids/DataGridInternal";
import {DataGridProps} from "./DataGrids/Types/DataGridProps";
import Import from "./DataGrids/Imports/Import";
import ExcelExport from "./DataGrids/Exports/ExcelExport";
import FieldsProvider from "../../../utils/DataGrid/FieldsProvider";
import Confirm, {smallDeleteProps} from "../Confirm";
import {DefaultDataGridProps} from "./DataGrids/Types/DefaultDataGridProps";
import {FieldProps} from "./DataGrids/Types/FieldProps";
import Button from "../Button";
import Portal from "../../layout/Portal";
import Modal from "../../layout/Modal";
import Select from "../Select";
import {FieldDataProps} from "./DataGrids/Types/FieldDataProps";

let defaultActions: DataGridProps['actions'] = [Import, ExcelExport];
let defaultPlugins: DataGridProps['plugins'] = [
    {
        type: "provider",
        plugin: FieldsProvider
    }
];

export const setDefaultActions = (actions: DataGridProps['actions']) => {
    defaultActions = actions;
}

export const setDefaultPlugins = (plugins: DataGridProps['plugins']) => {
    defaultPlugins = plugins;
}

export default class DataGrid<Props extends DataGridProps> extends DataGridInternal<Props> {
    constructor(props: Props) {
        super(props);
        this.setDefaultActions = this.setDefaultActions.bind(this);
        this.getDefaultActions = this.getDefaultActions.bind(this);
        this.setDefaultPlugins = this.setDefaultPlugins.bind(this);
        this.getDefaultPlugins = this.getDefaultPlugins.bind(this);
    }

    setDefaultActions(actions: DataGridProps['actions']) {
        defaultActions = actions;
    }

    getDefaultActions(): DataGridProps['actions'] {
        return Array.isArray(defaultActions) ? [...defaultActions] : defaultActions;
    }

    setDefaultPlugins(plugins: DataGridProps['plugins']) {
        defaultPlugins = plugins;
    }

    getDefaultPlugins(): DataGridProps['plugins'] {
        return Array.isArray(defaultPlugins) ? [...defaultPlugins] : defaultPlugins;
    }
}

export type ActionFieldsPropsType = {
    labelKey?: string,
    filter?: any,
    adderDialog?: React.ReactElement<{setOpened: (opened: boolean) => void, opened: boolean}&FieldDataProps>|React.FunctionComponent<{setOpened: (opened: boolean) => void, opened: boolean}&FieldDataProps>
}

const AddHeader: (props: ActionFieldsPropsType) => FieldProps['header'] = ({labelKey, filter, adderDialog: AdderDialog}: ActionFieldsPropsType = {}) => ({addItems, ...rest}) => {
    const component = rest?.componentListFieldConfig?.componentname ?? rest.componentConfig.componentname;
    const idfield = rest?.componentListFieldConfig?.idfield ?? rest.componentConfig.idfield ?? "id";
    const [opened, setOpened] = useState(false);
    const [value, setValue] = useState(null);
    return <Fragment>
        <Button
            size="xs"
            color="primary"
            onClick={() => setOpened(true)}
            icon={{lib: "fas", name: "plus"}}
            tooltip="Új hozzáadás"
        />
        {AdderDialog ? (AdderDialog.type ? <AdderDialog.type {...rest} idfield={idfield} component={component} labelKey={labelKey} filter={filter} adderDialog={AdderDialog} addItems={addItems} {...AdderDialog.props} opened={opened} setOpened={setOpened} /> : <AdderDialog {...rest} idfield={idfield} component={component} labelKey={labelKey} filter={filter} adderDialog={AdderDialog} addItems={addItems} opened={opened} setOpened={setOpened} />) : <Portal>
            <Modal
                header="Új hozzáadás"
                size="medium"
                state={opened}
                buttons={[
                    {
                        text: "Mégse",
                        props: {color: "default"},
                        onClose: true,
                        onClick: () => {
                            setValue(null);
                            setOpened(false);
                        }
                    },
                    {
                        text: "Hozzáadás",
                        props: {color: "primary"},
                        onClick: () => {
                            addItems([{[idfield] : value}]);
                            setValue(null);
                            setOpened(false);
                        }
                    }
                ]}
            >
                <div className="form-group">
                    <Select
                        label={false}
                        storeType="remote"
                        component={component}
                        labelKey={labelKey}
                        valueKey={idfield}
                        filter={filter}
                        objectValue={false}
                        value={value}
                        saveChanges={(field, value) => {
                            setValue(value);
                        }}
                    />
                </div>
            </Modal>
        </Portal>}
    </Fragment>
}

export const ActionField = (props: ActionFieldsPropsType = {}) => {
    return {
        "action": {
            props: {
                header: AddHeader(props),
                label: false,
                width: "auto",
                formatter: ({row, rowKey, removeItems}) => {
                    return <Confirm
                        {...smallDeleteProps}
                        onAccept={() => {
                            removeItems({[rowKey]: row});
                        }}
                    />
                }
            }
        }
    } as DefaultDataGridProps['fields'];
}